import { JSX } from 'react'

/**
 * プロジェクトのステータス型定義
 * 
 * 接頭辞の意味:
 * - draft_ : ユーザーによる入力・作成フェーズ
 * - operator_ : イロガミ社による作業フェーズ（ユーザーは待機）
 * - waiting_ : ユーザーのアクション待ちフェーズ
 * - preparing_ : イロガミ社による発送準備フェーズ
 * - collecting_ : 期間進行中フェーズ
 */
export type ProjectStatus =
  // 1. [ユーザー] 基本情報の入力中
  | 'draft_info'
  // 2. [ユーザー] 事業所リストの作成中
  | 'draft_list'
  // 3. [イロガミ] 郵送用データ作成・確認中（ユーザー編集不可）
  | 'operator_processing'
  // 4. [ユーザー] 作成されたデータの承認待ち（ユーザーのアクション必須）
  | 'waiting_for_approval'
  // 5. [イロガミ] はがき印刷・投函準備中
  | 'preparing_shipment'
  // 6. [期間] はがき発送済み・回答受付期間中
  | 'collecting_responses'
  // 7. [期間] 受付終了・集計フェーズ
  | 'collection_closed'
  // 8. [システム] キャンセル処理中（※必要であれば残す）
  | 'cancelling'
  // 9. [完了] 全工程終了
  | 'completed'
  // 10. [請求] 請求処理完了（admin向け）
  | 'billing_completed'

/**
 * ステータスの進行順序
 */
export const PROJECT_STATUS_ORDER: readonly ProjectStatus[] = [
  'draft_info',
  'draft_list',
  'operator_processing',
  'waiting_for_approval',
  'preparing_shipment',
  'collecting_responses',
  'collection_closed',
  'completed',
  'billing_completed',
] as const

/**
 * 学校画面で「案件の業務フロー上は終了」とみなすステータス。
 * - 案件一覧の「完了」タブ、ダッシュボードの進行中案件、お問い合わせの紐づけ候補などで共通利用する。
 */
export function isSchoolProjectWorkflowFinished(status: ProjectStatus): boolean {
  return status === 'completed' || status === 'billing_completed'
}

/**
 * ステータスの日本語ラベル
 */
export const getProjectStatusLabel = (status: ProjectStatus): string => {
  switch (status) {
    case 'draft_info':
      return '基本情報入力中' // "登録"より"入力中"の方が状態を表す
    case 'draft_list':
      return 'データ登録中'
    case 'operator_processing':
      return 'データ生成中' // イロガミ作業中
    case 'waiting_for_approval':
      return 'データ確認中' // 明確に「承認」というアクションを促す
    case 'preparing_shipment':
      return 'はがき郵送準備中（イロガミ）'
    case 'collecting_responses':
      return '回答受付中'
    case 'collection_closed':
      return '回答受付終了'
    case 'cancelling':
      return 'キャンセル処理中'
    case 'completed':
      return '職場体験終了'
    case 'billing_completed':
      return '請求確定'
    default:
      return status
  }
}

/**
 * 学校向けの日本語ラベル（管理画面の「請求確定」は出さず、利用者には職場体験終了と同じ文言に揃える）
 */
export function getSchoolProjectStatusLabel(status: ProjectStatus): string {
  if (status === 'billing_completed') return getProjectStatusLabel('completed')
  return getProjectStatusLabel(status)
}

/**
 * プロジェクトのステータスを表示用の日本語ラベルに変換（改行込み）
 * ステップインジケーターなど、狭い表示領域での使用を想定
 * @param status - ステータス
 * @returns 日本語ラベル（改行込み）
 */
export const getProjectStatusDisplayLabel = (status: ProjectStatus): string => {
  switch (status) {
    case 'draft_info':
      return '基本情報\n入力中'
    case 'draft_list':
      return 'データ登録中'
    case 'operator_processing':
      return 'データ生成中'
    case 'waiting_for_approval':
      return 'データ確認中'
    case 'preparing_shipment':
      return 'はがき郵送準備中\n(イロガミ)'
    case 'collecting_responses':
      return '回答受付中'
    case 'collection_closed':
      return '回答受付\n終了'
    case 'cancelling':
      return 'キャンセル\n処理中'
    case 'completed':
      return '職場体験終了'
    case 'billing_completed':
      return '請求確定'
    default:
      return status
  }
}

/**
 * 学校向けの表示用ラベル（改行込み）。請求確定も職場体験終了と同一表示。
 */
export function getSchoolProjectStatusDisplayLabel(status: ProjectStatus): string {
  if (status === 'billing_completed') return getProjectStatusDisplayLabel('completed')
  return getProjectStatusDisplayLabel(status)
}

/**
 * プロジェクトのステータスの説明を取得
 * @param status - ステータス
 * @returns ステータスの説明
 */
export const getProjectStatusDescription = (status: ProjectStatus): string => {
  switch (status) {
    case 'draft_info':
      return '職場体験の基本情報を入力してください。'
    case 'draft_list':
      return '職場体験を受け入れていただく事業所の情報を登録してください。'
    case 'operator_processing':
      return 'はがき郵送用のデータを生成・確認中です。'
    case 'waiting_for_approval':
      return '作成されたデータの承認をお待ちしています。'
    case 'preparing_shipment':
      return 'はがきの印刷・投函準備中です。'
    case 'collecting_responses':
      return '事業所からの回答を受付中です。'
    case 'collection_closed':
      return '事業所からの回答受付は終了しました。'
    case 'cancelling':
      return 'キャンセル処理中です。'
    case 'completed':
      return 'この案件は完了しています。'
    case 'billing_completed':
      return 'この案件は請求処理まで完了しています。'
    default:
      return ''
  }
}

/**
 * 案件番号バッジ（共通スタイル）
 */
export interface ProjectNumberBadgeProps {
  projectNumber: string
  size?: 'default' | 'compact'
  className?: string
}

export const ProjectNumberBadge = ({
  projectNumber,
  size = 'default',
  className,
}: ProjectNumberBadgeProps): JSX.Element => {
  const isCompact = size === 'compact'
  return (
    <span
      className={className}
      style={{
        display: 'inline-block',
        padding: isCompact ? '2px 6px' : '4px 12px',
        borderRadius: isCompact ? '3px' : '4px',
        fontSize: isCompact ? '0.65rem' : '12px',
        fontWeight: isCompact ? 500 : 'bold',
        lineHeight: '1.2',
        backgroundColor: '#f8f9fa',
      }}
    >
      {projectNumber}
    </span>
  )
}

/**
 * ステータスバッジ（色による区別をロジック化）
 */
interface ProjectStatusBadgeProps {
  status: ProjectStatus
  size?: 'default' | 'compact'
  /** 学校画面向け。請求確定も「職場体験終了」と同じ文言で表示する */
  forSchool?: boolean
}

export const ProjectStatusBadge = ({
  status,
  size = 'default',
  forSchool = false,
}: ProjectStatusBadgeProps): JSX.Element => {
  const getStatusStyle = () => {
    switch (status) {
      // school側の従来見た目に合わせ、進行中は同系色で統一
      case 'draft_info':
      case 'draft_list':
      case 'operator_processing':
      case 'waiting_for_approval':
      case 'preparing_shipment':
      case 'collecting_responses':
      case 'collection_closed':
        return { backgroundColor: '#fff3e0', color: '#e65100' }

      // 完了は緑
      case 'completed':
      case 'billing_completed':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' }

      // キャンセルは赤
      case 'cancelling':
        return { backgroundColor: '#ffebee', color: '#c62828' }
        
      default:
        return { backgroundColor: '#f8f9fa', color: '#212529' }
    }
  }

  const style = getStatusStyle()
  const isCompact = size === 'compact'

  return (
    <span
      style={{
        display: 'inline-block',
        padding: isCompact ? '2px 6px' : '4px 12px',
        borderRadius: isCompact ? '3px' : '4px',
        fontSize: isCompact ? '0.65rem' : '12px',
        fontWeight: isCompact ? 500 : 'bold',
        lineHeight: '1.2',
        ...style
      }}
    >
      {forSchool ? getSchoolProjectStatusLabel(status) : getProjectStatusLabel(status)}
    </span>
  )
}
